/* Sfeervuur story sections: reveal-on-scroll, scroll-linked effects and the small custom elements
   (benefits, scent picker, steps, marquee, embers).
   Markup: sections/sf-*.liquid   Styles: assets/sf-story.css
   Every effect checks the device tier (window.SF.perf.p, snippets/sf-perf.liquid) and follows
   it when the tier changes ("sf:perf"). */
(() => {
  'use strict';

  if (window.__sfStory) return;
  window.__sfStory = true;
  window.__sfStoryReady = true;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const rand = (a, b) => a + Math.random() * (b - a);
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mqNarrow = window.matchMedia('(max-width: 989.98px)');
  const reduced = () => mqReduce.matches;
  const TIER13 = { reveal: 2, scrub: true, count: true, parallax: true, tilt: true, magnet: true, marquee: true, boost: true, ambient: 1, sparks: 1, dpr: 1.5, emberFps: 40 };
  const P = () => (window.SF && window.SF.perf && window.SF.perf.p) || TIER13;
  const ping = (heavy) => window.SF && window.SF.perf && window.SF.perf.ping(500, heavy);
  // shortest gap between two drawn frames for a frame-rate cap; the slack lets a 144 Hz screen draw
  // every second refresh (72 fps) for a 60 fps cap instead of every third (48 fps)
  const minGap = (fps) => 1000 / fps - Math.min(4, 250 / fps);

  /* ------------------------------------------------------------------
     Scroll registry. Horizon scrolls `.page-wrapper` on desktop and the
     document on mobile, so listen to both. Registered elements are only
     measured while they are (nearly) on screen.
     ------------------------------------------------------------------ */

  const Loop = {
    items: new Set(),
    raf: 0,
    timer: 0,
    vel: 0,
    lastY: null,

    y() {
      const w = document.querySelector('.page-wrapper');
      return Math.max(window.scrollY || 0, w ? w.scrollTop : 0);
    },

    schedule() {
      if (this.raf) return;
      this.raf = window.requestAnimationFrame(() => this.run());
      // background tabs / hidden panes may never fire rAF; keep state correct anyway
      this.timer = window.setTimeout(() => this.run(), 120);
    },

    run() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      window.clearTimeout(this.timer);
      this.raf = 0;
      const vh = window.innerHeight;
      for (const it of this.items) {
        if (!it.vis) continue;
        it.fn(it.el.getBoundingClientRect(), vh);
      }
    },

    register(el, fn, margin = '25% 0px 25% 0px') {
      const it = { el, fn, vis: false };
      it.io = new IntersectionObserver(
        ([entry]) => {
          it.vis = entry.isIntersecting;
          if (it.vis) this.schedule();
        },
        { rootMargin: margin }
      );
      it.io.observe(el);
      this.items.add(it);
      return () => {
        it.io.disconnect();
        this.items.delete(it);
      };
    },

    onScroll() {
      const y = Loop.y();
      if (Loop.lastY !== null) Loop.vel = y - Loop.lastY;
      Loop.lastY = y;
      Loop.schedule();
    },
  };

  window.addEventListener('scroll', Loop.onScroll, { passive: true });
  window.addEventListener('resize', () => Loop.schedule(), { passive: true });
  const bindWrapper = () => {
    const w = document.querySelector('.page-wrapper');
    if (w && !w.__sfBound) {
      w.__sfBound = true;
      w.addEventListener('scroll', Loop.onScroll, { passive: true });
    }
  };
  bindWrapper();

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */

  const revealIO = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        (e.target.__sfReveal || [e.target]).forEach((el) => el.classList.add('is-in'));
        revealIO.unobserve(e.target);
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );

  /* ------------------------------------------------------------------
     Small behaviours driven by data attributes
     ------------------------------------------------------------------ */

  function initScrub(el) {
    const words = [...el.querySelectorAll('.sf-mw')];
    if (!words.length) return;
    if (reduced()) {
      words.forEach((w) => w.style.setProperty('--lit', '1'));
      return;
    }
    const n = words.length;
    const vals = new Array(n).fill(-1);
    Loop.register(el, (r, vh) => {
      // weakest tiers: the words are simply lit
      const p = P().scrub ? clamp((vh * 0.86 - r.top) / (vh * 0.4 + r.height), 0, 1) : 1;
      const k = p * (n + 2);
      for (let i = 0; i < n; i++) {
        const v = clamp(k - i, 0, 1);
        if (Math.abs(v - vals[i]) > 0.01) {
          vals[i] = v;
          words[i].style.setProperty('--lit', v.toFixed(2));
        }
      }
    });
    words.forEach((w) => w.style.setProperty('--lit', '0'));
  }

  function initCount(el) {
    const finalText = el.textContent.trim();
    if (!/\d/.test(finalText) || reduced() || !P().count) return;
    const nums = finalText.match(/\d+/g).map(Number);
    el.textContent = finalText.replace(/\d+/g, '0');
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const dur = 1900;
        const step = () => {
          const t = clamp((performance.now() - t0) / dur, 0, 1);
          const k = 1 - Math.pow(1 - t, 4);
          let i = 0;
          el.textContent = finalText.replace(/\d+/g, () => String(Math.round(nums[i++] * k)));
          if (t < 1) window.setTimeout(step, 32);
        };
        step();
      },
      { threshold: 0.6 }
    );
    io.observe(el);
  }

  // a quote that fills with gold line by line while it scrolls through the screen (CSS reads --p)
  function initSweep(el) {
    Loop.register(el, (r, vh) => {
      const p = reduced() || !P().scrub ? 1 : clamp((vh * 0.85 - r.top) / (r.height + vh * 0.3), 0, 1);
      el.style.setProperty('--p', p.toFixed(3));
    });
  }

  function initParallax(el) {
    const amt = parseFloat(el.dataset.sfPar) || 0.08;
    const target = el.querySelector('[data-sf-par-target]') || el;
    let moved = false;
    Loop.register(el, (r, vh) => {
      if (reduced() || !P().parallax) {
        if (moved) target.style.transform = '';
        moved = false;
        return;
      }
      moved = true;
      const t = clamp((r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2), -1, 1);
      target.style.transform = `translate3d(0, ${(-t * amt * r.height).toFixed(1)}px, 0)`;
    });
  }

  function initTilt(el) {
    if (reduced()) return;
    const tilt = el.querySelector('[data-sf-tilt-target]') || el;
    const max = 4;
    let raf = 0;
    let rx = 0;
    let ry = 0;
    let gx = 50;
    let gy = 40;
    const apply = () => {
      raf = 0;
      tilt.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
      tilt.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
      tilt.style.setProperty('--gx', `${gx.toFixed(1)}%`);
      tilt.style.setProperty('--gy', `${gy.toFixed(1)}%`);
    };
    el.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !P().tilt) return;
      const r = el.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      ry = nx * max * 2;
      rx = -ny * max * 2;
      gx = (nx + 0.5) * 100;
      gy = (ny + 0.5) * 100;
      tilt.classList.add('is-tilting');
      if (!raf) raf = window.requestAnimationFrame(apply);
    });
    el.addEventListener('pointerleave', () => {
      rx = 0;
      ry = 0;
      gx = 50;
      gy = 40;
      tilt.classList.remove('is-tilting');
      apply();
    });
  }

  function initMagnet(el) {
    if (reduced() || !mqFine.matches) return;
    el.addEventListener('pointermove', (e) => {
      if (!P().magnet) return;
      const r = el.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      el.style.setProperty('--tx', `${(dx * 12).toFixed(1)}px`);
      el.style.setProperty('--ty', `${(dy * 10).toFixed(1)}px`);
    });
    el.addEventListener('pointerleave', () => {
      el.style.setProperty('--tx', '0px');
      el.style.setProperty('--ty', '0px');
    });
  }

  // one record per behaviour: an element can carry several (data-sf-reveal + data-sf-par, or
  // data-sf-reveal + data-sf-faq from sf-shop.js), so a shared "done" marker would skip the others
  const bound = new Map();

  function scan(root) {
    bindWrapper();
    const mark = (sel, fn) => {
      let done = bound.get(sel);
      if (!done) bound.set(sel, (done = new WeakSet()));
      root.querySelectorAll(sel).forEach((el) => {
        if (done.has(el)) return;
        done.add(el);
        fn(el);
      });
    };

    mark('[data-sf-reveal]', (el) => {
      if (reduced() || P().reveal === 0) {
        el.classList.add('is-in');
        return;
      }
      // a fully clipped (mask) element counts as invisible to IntersectionObserver, so watch its parent
      const host = el.dataset.sfReveal === 'mask' && el.parentElement ? el.parentElement : el;
      (host.__sfReveal = host.__sfReveal || []).push(el);
      revealIO.observe(host);
    });
    mark('[data-sf-scrub]', initScrub);
    mark('[data-sf-sweep]', initSweep);
    mark('[data-sf-count]', initCount);
    mark('[data-sf-par]', initParallax);
    mark('[data-sf-tilt]', initTilt);
    mark('[data-sf-magnet]', initMagnet);
  }

  /* ------------------------------------------------------------------
     <sf-benefits>: sticky image follows the benefit in the middle of the screen
     ------------------------------------------------------------------ */

  class SFBenefits extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.sf-ben__item')];
      this.imgs = [...this.querySelectorAll('.sf-ben__img')];
      this.counter = this.querySelector('[data-cur]');
      this.list = this.querySelector('.sf-ben__list');
      this.active = -1;
      this.set(0);

      this.io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) this.set(this.items.indexOf(e.target));
          }
        },
        { rootMargin: '-46% 0px -46% 0px' }
      );
      this.items.forEach((it) => this.io.observe(it));

      if (this.list) {
        this.unreg = Loop.register(this.list, (r, vh) => {
          this.list.style.setProperty('--p', clamp((vh * 0.5 - r.top) / r.height, 0, 1).toFixed(3));
        });
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.unreg) this.unreg();
    }

    set(i) {
      if (i < 0 || i === this.active) return;
      this.active = i;
      this.items.forEach((it, k) => it.classList.toggle('is-active', k === i));
      this.imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
      if (this.counter) this.counter.textContent = String(i + 1).padStart(2, '0');
    }
  }

  /* ------------------------------------------------------------------
     <sf-scents>: scent picker with autoplay until the visitor takes over
     ------------------------------------------------------------------ */

  class SFScents extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('.sf-scent-tab')];
      this.slides = [...this.querySelectorAll('.sf-scent')];
      this.bar = this.querySelector('.sf-scents__bar i');
      this.dur = 5500;
      this.idx = 0;
      this.touched = false;
      this.visible = false;
      this.timer = 0;

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          this.touched = true;
          this.select(i);
        });
        tab.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.touched = true;
          this.select(i);
        });
        tab.addEventListener('keydown', (e) => {
          const n = this.tabs.length;
          let next = -1;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % n;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + n) % n;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = n - 1;
          if (next < 0) return;
          e.preventDefault();
          this.touched = true;
          this.select(next);
          this.tabs[next].focus();
        });
      });

      this.select(0);

      this.io = new IntersectionObserver(
        ([e]) => {
          this.visible = e.isIntersecting;
          if (this.visible) this.arm();
          else window.clearTimeout(this.timer);
        },
        { threshold: 0.35 }
      );
      this.io.observe(this);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (this.io) this.io.disconnect();
    }

    select(i) {
      this.idx = i;
      this.tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', String(k === i));
        t.tabIndex = k === i ? 0 : -1;
      });
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));

      // nudge the active control into view without moving the page itself --
      // a no-op wherever the tab list already fits on screen
      const tab = this.tabs[i];
      if (tab) tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
      this.arm();
    }

    arm() {
      window.clearTimeout(this.timer);
      if (this.bar) {
        this.bar.classList.remove('is-running');
        // restart the CSS animation
        void this.bar.offsetWidth;
      }
      if (this.touched || reduced() || !this.visible) return;
      if (this.bar) {
        this.bar.style.setProperty('--dur', `${this.dur}ms`);
        this.bar.classList.add('is-running');
      }
      this.timer = window.setTimeout(() => {
        if (this.touched) return;
        this.select((this.idx + 1) % this.tabs.length);
      }, this.dur);
    }
  }

  /* ------------------------------------------------------------------
     <sf-steps>: gold line fills while the steps scroll past
     ------------------------------------------------------------------ */

  class SFSteps extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('.sf-step')];
      this.track = this.querySelector('.sf-steps__track');
      if (!this.track) return;

      if (reduced()) {
        this.track.style.setProperty('--p', '1');
        this.steps.forEach((s) => s.classList.add('is-on'));
        return;
      }

      const n = this.steps.length;
      this.unreg = Loop.register(this.track, (r, vh) => {
        const p = mqNarrow.matches
          ? clamp((vh * 0.62 - r.top) / r.height, 0, 1)
          : clamp((vh * 0.86 - r.top) / (vh * 0.36), 0, 1);
        this.track.style.setProperty('--p', p.toFixed(3));
        this.steps.forEach((s, i) => s.classList.toggle('is-on', p >= (n > 1 ? i / (n - 1) : 0) - 0.001));
      });
    }

    disconnectedCallback() {
      if (this.unreg) this.unreg();
    }
  }

  /* ------------------------------------------------------------------
     <sf-marquee>: endless ribbon that speeds up while scrolling
     ------------------------------------------------------------------ */

  class SFMarquee extends HTMLElement {
    connectedCallback() {
      if (reduced()) return;
      this.track = this.querySelector('.sf-marq__track');
      this.group = this.track && this.track.firstElementChild;
      if (!this.group) return;

      this.x = 0;
      this.gw = 0;
      this.last = 0;
      this.raf = 0;
      this.boost = 0;
      this.speed = parseFloat(this.dataset.speed) || 55;

      this.ro = new ResizeObserver(() => (this.gw = this.group.getBoundingClientRect().width));
      this.ro.observe(this.group);

      this.visible = false;
      this.io = new IntersectionObserver(([e]) => {
        this.visible = e.isIntersecting;
        if (this.visible) this.start();
        else this.stop();
      });
      this.io.observe(this);
      this.frame = this.frame.bind(this);
      // lowest tiers: the ribbon stands still
      this.onPerf = () => (this.visible && P().marquee ? this.start() : this.stop());
      document.addEventListener('sf:perf', this.onPerf);
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.onPerf) document.removeEventListener('sf:perf', this.onPerf);
    }

    start() {
      if (this.raf || !this.group || !P().marquee) return;
      this.last = 0;
      this.raf = window.requestAnimationFrame(this.frame);
    }

    stop() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    frame(now) {
      this.raf = 0;
      const dt = Math.min(0.05, this.last ? (now - this.last) / 1000 : 0.016);
      this.last = now;
      const v = P().boost ? Math.abs(Loop.vel) : 0;
      Loop.vel *= 0.9;
      this.boost += (Math.min(v, 90) * 9 - this.boost) * 0.1;
      ping(false);
      this.x -= (this.speed + this.boost) * dt;
      if (this.gw && this.x <= -this.gw) this.x += this.gw;
      this.track.style.transform = `translate3d(${this.x.toFixed(2)}px, 0, 0)`;
      this.raf = window.requestAnimationFrame(this.frame);
    }
  }

  /* ------------------------------------------------------------------
     <sf-embers>: rising sparks over a photo
     ------------------------------------------------------------------ */

  class SFEmbers extends HTMLElement {
    connectedCallback() {
      if (reduced()) return;
      this.canvas = this.querySelector('canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this.ambient = this.dataset.mode === 'ambient';
      const [ox, oy] = (this.dataset.origin || '0.5,0.85').split(',').map(Number);
      this.ox = ox;
      this.oy = oy;
      this.spread = parseFloat(this.dataset.spread) || 0.2;
      // base values for tier 13; the tier multiplier is applied every frame (see mul())
      this.rate = parseFloat(this.dataset.rate) || (this.ambient ? 1.6 : 12);
      this.cap = this.ambient ? 40 : 90;
      this.dpr = 0;
      this.parts = [];
      this.acc = 0;
      this.last = 0;
      this.raf = 0;
      this.w = 0;
      this.h = 0;

      const sprite = document.createElement('canvas');
      sprite.width = sprite.height = 32;
      const g = sprite.getContext('2d');
      const gr = g.createRadialGradient(16, 16, 0, 16, 16, 16);
      gr.addColorStop(0, 'rgba(255,244,214,1)');
      gr.addColorStop(0.25, 'rgba(255,178,84,.85)');
      gr.addColorStop(1, 'rgba(255,110,20,0)');
      g.fillStyle = gr;
      g.fillRect(0, 0, 32, 32);
      this.sprite = sprite;

      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this);
      // the canvas is as tall as the screen at most, so a new window height means a new canvas
      this.onWinResize = () => {
        if (Math.min(this.h, window.innerHeight) !== this.ch) this.resize();
      };
      window.addEventListener('resize', this.onWinResize, { passive: true });
      this.visible = false;
      this.io = new IntersectionObserver(([e]) => {
        this.visible = e.isIntersecting;
        if (this.visible) this.start();
        else this.stop();
      });
      this.io.observe(this);
      this.frame = this.frame.bind(this);
      this.onPerf = () => {
        this.resize();
        if (this.visible && this.mul() > 0) this.start();
        else this.stop(true);
      };
      document.addEventListener('sf:perf', this.onPerf);
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.onPerf) document.removeEventListener('sf:perf', this.onPerf);
      if (this.onWinResize) window.removeEventListener('resize', this.onWinResize);
    }

    // how much of the tier-13 spark rate this device gets (0 = none)
    mul() {
      const p = P();
      return this.ambient ? p.ambient : p.sparks;
    }

    // The canvas is never taller than the screen: it sticks to the top of the viewport while the
    // section scrolls past (CSS "sf-embers canvas { position: sticky }") and the particles, which
    // live in section coordinates, are drawn relative to where it sits. A 4000px section used to
    // mean a 4000px canvas cleared and re-uploaded every frame.
    resize() {
      // ambient specks are soft dots, 1x is plenty; the brighter rising sparks follow the tier
      const dpr = Math.min(window.devicePixelRatio || 1, this.ambient ? 1 : P().dpr);
      this.w = this.clientWidth;
      this.h = this.clientHeight;
      this.ch = Math.max(2, Math.min(this.h, window.innerHeight || this.h));
      this.dpr = dpr;
      this.canvas.style.height = `${this.ch}px`;
      this.canvas.width = Math.max(2, Math.round(this.w * dpr));
      this.canvas.height = Math.max(2, Math.round(this.ch * dpr));
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.dirty = false;
    }

    start() {
      if (this.raf || !(this.mul() > 0)) return;
      this.last = 0;
      this.raf = window.requestAnimationFrame(this.frame);
    }

    stop(clear) {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (clear) {
        this.parts.length = 0;
        this.ctx.clearRect(0, 0, this.w, this.ch);
        this.dirty = false;
      }
    }

    frame(now) {
      this.raf = 0;
      // slow drifting specks need 30-60 fps, not the screen's 120-144
      if (this.last && now - this.last < minGap(P().emberFps || 40)) {
        this.raf = window.requestAnimationFrame(this.frame);
        return;
      }
      const dt = Math.min(0.05, this.last ? (now - this.last) / 1000 : 0.016);
      this.last = now;
      const { ctx, w, h, ch } = this;
      const mul = this.mul();
      // where the sticky canvas sits inside the section right now (section coords of its top edge)
      const off = this.canvas.getBoundingClientRect().top - this.getBoundingClientRect().top;
      if (this.dirty) ctx.clearRect(0, 0, w, ch);
      this.dirty = false;
      ctx.globalCompositeOperation = 'lighter';
      ping(true);

      this.acc += this.rate * mul * dt;
      while (this.acc >= 1) {
        this.acc -= 1;
        if (this.parts.length > this.cap * Math.max(1, mul)) break;
        this.parts.push(
          this.ambient
            ? {
                x: rand(0, 1) * w,
                y: rand(0, 1) * h,
                vx: rand(-3, 3),
                vy: -rand(2, 7),
                sway: 3,
                age: 0,
                life: rand(4.5, 8.5),
                size: rand(1.3, 2.6),
                mult: 1.9,
                peak: 0.55,
                ph: rand(0, 6.28),
              }
            : {
                x: (this.ox + rand(-this.spread, this.spread)) * w,
                y: (this.oy + rand(-0.03, 0.03)) * h,
                vx: rand(-8, 8),
                vy: -rand(26, 70),
                sway: 11,
                age: 0,
                life: rand(2.6, 5.2),
                size: rand(2, 4.6),
                mult: 2.2,
                peak: 0.85,
                ph: rand(0, 6.28),
              }
        );
      }

      for (let i = this.parts.length - 1; i >= 0; i--) {
        const p = this.parts[i];
        p.age += dt;
        if (p.age >= p.life || p.y < -10) {
          this.parts.splice(i, 1);
          continue;
        }
        p.x += (p.vx + Math.sin(p.age * 2.4 + p.ph) * p.sway) * dt;
        p.y += p.vy * dt;
        const t = p.age / p.life;
        // soft envelope: fade in, hold, fade out -- never a hard pop in or out
        const envelope = Math.min(clamp(t / 0.22, 0, 1), clamp((1 - t) / 0.35, 0, 1));
        const r = p.size * (0.7 + 0.3 * envelope) * p.mult;
        const y = p.y - off;
        if (y < -r || y > ch + r) continue; // outside the screen-sized canvas
        ctx.globalAlpha = envelope * p.peak;
        ctx.drawImage(this.sprite, p.x - r, y - r, r * 2, r * 2);
        this.dirty = true;
      }
      ctx.globalAlpha = 1;
      this.raf = window.requestAnimationFrame(this.frame);
    }
  }

  /* ------------------------------------------------------------------
     <sf-timeline>: a gold line fills while the chapters scroll past, a spark rides its tip and each
     chapter lights up once the spark has reached it (about page, sections/sf-about-story.liquid)
     ------------------------------------------------------------------ */

  class SFTimeline extends HTMLElement {
    connectedCallback() {
      this.chapters = [...this.querySelectorAll('[data-sf-ch]')];
      this.unreg = Loop.register(
        this,
        (r, vh) => {
          const still = reduced() || P().reveal === 0;
          const p = still ? 1 : clamp((vh * 0.55 - r.top) / r.height, 0, 1);
          this.style.setProperty('--p', p.toFixed(4));
          this.style.setProperty('--spark', !still && p > 0.004 && p < 0.996 ? '1' : '0');
          const tip = p * r.height;
          for (const ch of this.chapters) ch.classList.toggle('is-lit', still || tip >= ch.offsetTop + ch.offsetHeight / 2);
        },
        '15% 0px 15% 0px'
      );
    }

    disconnectedCallback() {
      if (this.unreg) this.unreg();
    }
  }

  customElements.define('sf-timeline', SFTimeline);
  customElements.define('sf-benefits', SFBenefits);
  customElements.define('sf-scents', SFScents);
  customElements.define('sf-steps', SFSteps);
  customElements.define('sf-marquee', SFMarquee);
  customElements.define('sf-embers', SFEmbers);

  const boot = () => scan(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (e) => scan(e.target));

  mqReduce.addEventListener &&
    mqReduce.addEventListener('change', () => {
      if (reduced()) document.querySelectorAll('[data-sf-reveal]').forEach((el) => el.classList.add('is-in'));
    });

  // the device tier changed: show anything still waiting for a reveal the tier no longer does,
  // and let scroll-linked effects (scrub, parallax) settle into their new mode
  document.addEventListener('sf:perf', () => {
    if (P().reveal === 0) document.querySelectorAll('[data-sf-reveal]').forEach((el) => el.classList.add('is-in'));
    Loop.schedule();
  });
})();
